/**
 * Niche decks (/deck/<slug>): the same story, retold for one audience with
 * their own portfolio growth stories. Hooks over paragraphs — one idea per slide.
 */
import {
  COMPANY_DECK,
  type DeckDefinition,
  type DeckFlow,
  type DeckGrowthArc,
  type DeckRow,
  type DeckSlide,
} from "@/lib/deck/content";

const SHOTS = "/media/portfolio/screenshots";

type GrowthStory = {
  slug: string;
  client: string;
  label: string;
  hook: string;
  arc: DeckGrowthArc;
};

type NicheInput = {
  slug: string;
  label: string;
  audience: string;
  title: string;
  description: string;
  cover: { label: string; title: string; accent: string; body: string; chips: string[]; image: string; imageAlt: string };
  hook: { title: string; accent: string };
  leak: { title: string; rows: DeckRow[] };
  stories: GrowthStory[];
  ai: { title: string; flows: DeckFlow[] };
  cta: { title: string; accent: string; body: string };
};

function nicheDeck(n: NicheInput): DeckDefinition {
  const url = `https://doimpo.com/deck/${n.slug}`;
  const slides: DeckSlide[] = [
    {
      kind: "cover",
      id: "cover",
      label: n.cover.label,
      title: n.cover.title,
      accent: n.cover.accent,
      body: n.cover.body,
      chips: n.cover.chips,
      image: n.cover.image,
      imageAlt: n.cover.imageAlt,
    },
    { kind: "hook", id: "the-truth", label: "The truth", title: n.hook.title, accent: n.hook.accent },
    { kind: "compare", id: "the-leak", label: "Where growth leaks", title: n.leak.title, left: "Today", right: "With doimpo", rows: n.leak.rows },
    ...n.stories.map(
      (s): DeckSlide => ({
        kind: "growth",
        id: `growth-${s.slug}`,
        label: `Growth story · ${s.label}`,
        client: s.client,
        hook: s.hook,
        arc: s.arc,
        image: `${SHOTS}/${s.slug}-home.webp`,
        mobileImage: `${SHOTS}/${s.slug}-home-mobile.webp`,
        imageAlt: `${s.client} website, designed and built by doimpo`,
        href: `/portfolio/${s.slug}`,
      }),
    ),
    { kind: "flows", id: "on-autopilot", label: "On autopilot", title: n.ai.title, flows: n.ai.flows },
    {
      kind: "cta",
      id: "lets-talk",
      label: "Let's talk",
      title: n.cta.title,
      accent: n.cta.accent,
      body: n.cta.body,
      image: "/media/images/deck/deck-cta.webp",
      imageAlt: "A relaxed founder smiling on a video call at a tidy desk",
    },
  ];
  return {
    slug: n.slug,
    path: `/deck/${n.slug}`,
    url,
    title: n.title,
    description: n.description,
    ogImage: `https://doimpo.com/media/brand/og-doimpo-deck-${n.slug}.jpg`,
    ogImageAlt: `doimpo for ${n.audience} — ${n.cover.title} ${n.cover.accent}`,
    label: n.label,
    campaign: `deck-${n.slug}`,
    shareText: `${n.cover.title} ${n.cover.accent} How doimpo grows ${n.audience.toLowerCase()}.`,
    slides,
  };
}

export const NICHE_DECKS: DeckDefinition[] = [
  nicheDeck({
    slug: "healthcare",
    label: "Healthcare deck",
    audience: "Clinics & hospitals",
    title: "doimpo for Healthcare — Patients Choose Before They Call",
    description:
      "How doimpo helps clinics, hospitals and specialists turn clinical expertise into patient trust: patient journeys, authority content, clinic websites and an AI front desk — with real growth stories.",
    cover: {
      label: "For clinics & hospitals",
      title: "Patients choose",
      accent: "before they call.",
      body: "We make your expertise visible — so trust starts before the first consult.",
      chips: ["Patient journeys", "Clinic websites", "Authority content", "AI front desk"],
      image: "/media/images/deck/deck-healthcare.webp",
      imageAlt: "A smiling doctor reviewing results on a tablet with a patient at dusk",
    },
    hook: { title: "Excellent care.", accent: "Invisible online." },
    leak: {
      title: "Where patients slip away.",
      rows: [
        { from: "A generic hospital site", to: "Specialists patients trust" },
        { from: "Enquiries lost after hours", to: "Answered and booked, 24/7" },
        { from: "Expertise buried in PDFs", to: "Content that earns the consult" },
        { from: "Guessing what works", to: "Every enquiry traced to its source" },
      ],
    },
    stories: [
      {
        slug: "sali",
        client: "South Asian Liver Institute",
        label: "Specialist institute",
        hook: "Specialist liver care, finally visible.",
        arc: ["Expertise hidden behind a dated site", "Mapped the patient journey, rebuilt the door", "Authority content → qualified consults"],
      },
      {
        slug: "ashok-one-hospital",
        client: "Ashok One Hospital",
        label: "Hospital",
        hook: "A hospital patients can navigate in one tap.",
        arc: ["Services hard to find", "One brand, clear patient paths", "Services → doctors → enquiry"],
      },
      {
        slug: "tom-cherian",
        client: "Prof. Dr. Tom Cherian",
        label: "Surgeon",
        hook: "A surgeon's reputation, owned — not scattered.",
        arc: ["Awards spread across the web", "One distinguished home online", "Peers and patients → consultations"],
      },
    ],
    ai: {
      title: "Your front desk never sleeps.",
      flows: [
        { trigger: "Patient asks at midnight", action: "Answered from your own content", outcome: "Booked by morning" },
        { trigger: "Missed call", action: "Instant WhatsApp follow-up", outcome: "No patient lost" },
        { trigger: "Consult finished", action: "Care notes and reminders sent", outcome: "Patients come back" },
      ],
    },
    cta: {
      title: "Let's fill your",
      accent: "consult calendar.",
      body: "30 minutes. We'll map exactly where your patients slip away.",
    },
  }),
  nicheDeck({
    slug: "hospitality",
    label: "Hospitality & events deck",
    audience: "Hotels, venues & events",
    title: "doimpo for Hospitality & Events — Every Booking Should Come Home",
    description:
      "How doimpo helps hotels, venues and event brands win direct bookings and sell seats they own: hospitality brands, direct-booking sites, ticketing platforms and campaigns — with real growth stories.",
    cover: {
      label: "For hotels, venues & events",
      title: "Every booking",
      accent: "should come home.",
      body: "Brand, site and campaigns that turn lookers into guests — on your terms.",
      chips: ["Direct booking", "Ticketing", "Hospitality brand", "Campaigns"],
      image: "/media/images/deck/deck-hospitality.webp",
      imageAlt: "A hotel host warmly welcoming a smiling couple at a boutique reception at dusk",
    },
    hook: { title: "They find you on Instagram.", accent: "Then book somewhere else." },
    leak: {
      title: "Where bookings leak.",
      rows: [
        { from: "OTAs own your guests", to: "Direct bookings you keep" },
        { from: "Tables sold in WhatsApp threads", to: "Seat maps and secure checkout" },
        { from: "Instagram as your website", to: "A home that sells the stay" },
        { from: "One night, then silence", to: "Guests who come back" },
      ],
    },
    stories: [
      {
        slug: "livinhotels",
        client: "The Livin Hotels",
        label: "Boutique hotel",
        hook: "A hotel site that feels like the stay.",
        arc: ["Guests booking through OTAs", "Branded the stay, built the direct site", "Campaigns → direct bookings"],
      },
      {
        slug: "tiktzphoenix",
        client: "ZPhoenix Events",
        label: "Premium events",
        hook: "Premium nights, sold seat by seat.",
        arc: ["Tables booked over WhatsApp", "Own ticketing with live seat maps", "Discover → pick a seat → checkout"],
      },
      {
        slug: "reign",
        client: "Reign Conference",
        label: "Conference",
        hook: "A conference that fills its own room.",
        arc: ["Strong community, no front door", "Gold-on-dark brand, one landing page", "Every channel → registration"],
      },
    ],
    ai: {
      title: "Guests answered. Even at 2am.",
      flows: [
        { trigger: "“Free this weekend?” at 2am", action: "Availability answered instantly", outcome: "Booked direct" },
        { trigger: "New event announced", action: "Posts, reels and emails drafted", outcome: "Every channel live in a day" },
        { trigger: "Stay or show ends", action: "Thank-you and review request", outcome: "Reviews and return visits" },
      ],
    },
    cta: {
      title: "Let's bring your",
      accent: "guests home.",
      body: "30 minutes. We'll show you where your bookings leak — and how to keep them.",
    },
  }),
  nicheDeck({
    slug: "ecommerce",
    label: "E-commerce deck",
    audience: "Retail & e-commerce brands",
    title: "doimpo for E-commerce — Stop Renting Your Customers",
    description:
      "How doimpo helps retail and e-commerce brands build stores people remember: brand systems, storefronts, campaigns and content that turn marketplaces into a channel, not the business — with real growth stories.",
    cover: {
      label: "For brands that sell online",
      title: "Stop renting",
      accent: "your customers.",
      body: "A brand and store people remember — so marketplaces become a channel, not the business.",
      chips: ["Brand", "Storefront", "Campaigns", "Content"],
      image: "/media/images/deck/deck-commerce.webp",
      imageAlt: "A smiling founder wrapping a customer order in her calm studio at dusk",
    },
    hook: { title: "Marketplaces sell your product.", accent: "Only you can sell your brand." },
    leak: {
      title: "Where margin leaks.",
      rows: [
        { from: "Lost in a marketplace grid", to: "A store that feels curated" },
        { from: "Ads land on a generic page", to: "Campaigns land on your brand" },
        { from: "Customers belong to the platform", to: "Customers you can reach again" },
        { from: "Product photos, no story", to: "Content that makes people want it" },
      ],
    },
    stories: [
      {
        slug: "plantsmark",
        client: "Plant's Mark",
        label: "Botanical store",
        hook: "Plants that sell like gallery pieces.",
        arc: ["Too rustic or too dropship", "Deep green and gold, a gallery store", "Campaigns → a store that feels premium"],
      },
      {
        slug: "keencouture",
        client: "Keen Couture",
        label: "Fashion house",
        hook: "A fashion house with its own front door.",
        arc: ["Margin leaking to marketplaces", "House mark and a digital atelier", "Lookbooks → collections → checkout"],
      },
      {
        slug: "trevia",
        client: "Trevia EV",
        label: "Electric mobility",
        hook: "An EV brand buyers trust at first glance.",
        arc: ["Buyers weighing range, price and trust", "An established wordmark and showroom site", "Campaigns → showroom → enquiry"],
      },
    ],
    ai: {
      title: "Your store, working the night shift.",
      flows: [
        { trigger: "Cart left behind", action: "A personal nudge, not spam", outcome: "Sales recovered" },
        { trigger: "New product shot", action: "Listing, posts and ads written", outcome: "Live everywhere in a day" },
        { trigger: "“Will this fit me?”", action: "Answered from your catalogue", outcome: "Fewer returns" },
      ],
    },
    cta: {
      title: "Let's build a brand",
      accent: "people come back to.",
      body: "30 minutes. We'll find the margin you're leaving on someone else's platform.",
    },
  }),
  nicheDeck({
    slug: "saas",
    label: "SaaS & AI deck",
    audience: "SaaS & AI products",
    title: "doimpo for SaaS & AI — Great Product, Explained in One Screen",
    description:
      "How doimpo helps SaaS and AI founders turn curious visitors into active users: positioning, brand, product sites and activation — with real growth stories.",
    cover: {
      label: "For SaaS & AI founders",
      title: "Great product.",
      accent: "Explained in one screen.",
      body: "Positioning, brand and a site that turn curious visitors into active users.",
      chips: ["Positioning", "Brand", "Product site", "Activation"],
      image: "/media/images/deck/deck-saas.webp",
      imageAlt: "Two founders delighted as their product finally clicks on screen, late at night",
    },
    hook: { title: "If they need a demo to get it,", accent: "they won't." },
    leak: {
      title: "Where sign-ups stall.",
      rows: [
        { from: "A wall of features", to: "One clear promise" },
        { from: "Looks like every other SaaS", to: "A brand they remember" },
        { from: "Sign up, then silence", to: "A first win in minutes" },
        { from: "Site and product feel different", to: "One story, end to end" },
      ],
    },
    stories: [
      {
        slug: "execura",
        client: "Execura",
        label: "AI workspace",
        hook: "From idea to execution — sold in one line.",
        arc: ["Founders drowning in scattered tools", "Named the 28-step Idea Journey", "Curiosity → workspace sign-ups"],
      },
      {
        slug: "debtor",
        client: "DebtClear AI",
        label: "Fintech AI",
        hook: "Debt anxiety → a payoff plan, in one upload.",
        arc: ["Fintech that looked like a bank", "Signature Violet and a single job", "Upload → plan → weekly habit"],
      },
      {
        slug: "mairaki",
        client: "mairaki",
        label: "Habit app",
        hook: "A habit app that feels like calm, not neon.",
        arc: ["Habit apps shouting streaks", "Emerald brand, a simple m-mark", "Web introduces → app keeps the streak"],
      },
    ],
    ai: {
      title: "Growth loops that run themselves.",
      flows: [
        { trigger: "New sign-up", action: "Onboarding tailored by AI", outcome: "First win, faster" },
        { trigger: "Release shipped", action: "Changelog, posts and emails drafted", outcome: "Users actually notice" },
        { trigger: "User goes quiet", action: "A usage-based nudge", outcome: "Churn caught early" },
      ],
    },
    cta: {
      title: "Let's make your product",
      accent: "obvious.",
      body: "30 minutes. We'll pressure-test your one-line promise.",
    },
  }),
  nicheDeck({
    slug: "personal-brands",
    label: "Personal brand deck",
    audience: "Founders, experts & creators",
    title: "doimpo for Personal Brands — Your Name Is Your Best Asset",
    description:
      "How doimpo helps founders, experts and creators turn reputation into invitations: personal brands, owned websites and content systems — with real growth stories.",
    cover: {
      label: "For founders, experts & creators",
      title: "Your name",
      accent: "is your best asset.",
      body: "A brand and a home online that turn reputation into invitations.",
      chips: ["Personal brand", "Website", "Content", "Speaking & press"],
      image: "/media/images/deck/deck-personal-brand.webp",
      imageAlt: "A founder laughing while filming content in a warm studio",
    },
    hook: { title: "Your audience lives on platforms.", accent: "Your reputation shouldn't." },
    leak: {
      title: "Where opportunities leak.",
      rows: [
        { from: "Proof buried in LinkedIn threads", to: "Proof on page one" },
        { from: "Instagram as your homepage", to: "A home you own" },
        { from: "DMs as your booking system", to: "Clear ways to work with you" },
        { from: "Posting without a plan", to: "Content that earns the call" },
      ],
    },
    stories: [
      {
        slug: "ameymarathe",
        client: "Chef Amey Marathe",
        label: "Chef",
        hook: "From the feed to a table he owns.",
        arc: ["Instagram as the only homepage", "Signature lockups and a chef platform", "Audience → guests, collabs, press"],
      },
      {
        slug: "the-n",
        client: "Samuel Susheel Nyalakanti",
        label: "Growth marketer",
        hook: "A marketing leader whose proof speaks first.",
        arc: ["Results buried in LinkedIn", "Clear positioning across markets", "Insights → authority → calls"],
      },
      {
        slug: "davidenoch",
        client: "David Enoch",
        label: "Builder & mentor",
        hook: "Two callings. One home.",
        arc: ["Product and people, split apart", "One narrative for both", "Products and writing → invitations"],
      },
    ],
    ai: {
      title: "One idea. A week of presence.",
      flows: [
        { trigger: "One talk or podcast", action: "Cut into clips, posts and a newsletter", outcome: "A week of content" },
        { trigger: "“Can we work together?”", action: "Qualified and booked for you", outcome: "Only the right calls" },
        { trigger: "Press mention lands", action: "Added to your site and shared", outcome: "Proof that compounds" },
      ],
    },
    cta: {
      title: "Let's make your name",
      accent: "work for you.",
      body: "30 minutes. We'll map the shortest path from reputation to invitations.",
    },
  }),
];

export const ALL_DECKS: DeckDefinition[] = [COMPANY_DECK, ...NICHE_DECKS];

export function getDeck(slug?: string): DeckDefinition | undefined {
  if (!slug) return COMPANY_DECK;
  return NICHE_DECKS.find((d) => d.slug === slug);
}
